import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { AnalyticsPageSkeleton } from '../components/skeleton/PageSkeletons';

const API_BASE = 'https://api.alburaqscents.com/v1/analytics';

const PIE_COLORS = ['#F9D978', '#3FD5D1', '#A78BFA', '#34D399', '#F472B6', '#F27069', '#60A5FA', '#FBBF24'];

// Short readable label for chart/table (no long URLs or UTM params). productName from API when path is /product/:id
function pagePathToLabel(path, productName) {
  if (!path || path === '/') return 'Home';
  const pathname = path.indexOf('?') >= 0 ? path.slice(0, path.indexOf('?')) : path;
  const query = path.indexOf('?') >= 0 ? path.slice(path.indexOf('?') + 1) : '';
  if (pathname === '/cart') return 'Cart';
  if (pathname === '/checkout') return 'Checkout';
  if (pathname === '/shop') {
    const params = new URLSearchParams(query);
    const cat = params.get('category');
    const newArr = params.get('newArrivals');
    if (cat) return `Shop (${cat})`;
    if (newArr) return 'Shop (New arrivals)';
    return 'Shop';
  }
  if (pathname.startsWith('/product/')) return productName ? `Product: ${productName}` : 'Product';
  if (pathname === '/new-arrivals') return 'New arrivals';
  if (pathname === '/deals') return 'Deals';
  if (pathname === '/gift-box') return 'Gift box';
  if (pathname.length > 24) return pathname.slice(0, 21) + '…';
  return pathname || 'Page';
}

function toYYYYMMDD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getToday() {
  return toYYYYMMDD(new Date());
}

function Analytics() {
  const today = getToday();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedFrom, setAppliedFrom] = useState(today);
  const [appliedTo, setAppliedTo] = useState(today);

  const [summary, setSummary] = useState(null);
  const [visitsOverTime, setVisitsOverTime] = useState([]);
  const [categoryOverTime, setCategoryOverTime] = useState({ data: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setError(null);
    try {
      const url = `${API_BASE}/summary?from=${appliedFrom}&to=${appliedTo}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.success) setSummary(result.data);
      else setError(result.message || 'Failed to load analytics');
    } catch (err) {
      setError(err.message || 'Error fetching analytics');
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    setLoading(true);
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const url = `${API_BASE}/visits-over-time?from=${appliedFrom}&to=${appliedTo}`;
    fetch(url)
      .then((r) => r.json())
      .then((result) => result.success && setVisitsOverTime(result.data || []))
      .catch(() => setVisitsOverTime([]));
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    const url = `${API_BASE}/category-visits-over-time?from=${appliedFrom}&to=${appliedTo}`;
    fetch(url)
      .then((r) => r.json())
      .then((result) => {
        if (result.success) setCategoryOverTime({ data: result.data || [], categories: result.categories || [] });
      })
      .catch(() => setCategoryOverTime({ data: [], categories: [] }));
  }, [appliedFrom, appliedTo]);

  const applyDateRange = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setLoading(true);
  };

  const resetToToday = () => {
    setDateFrom(today);
    setDateTo(today);
    setAppliedFrom(today);
    setAppliedTo(today);
    setLoading(true);
  };

  if (loading && !summary) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Website Analytics</h1>
          <p style={styles.headerSubtitle}>Visits, cart & checkout by date range</p>
        </div>
        <AnalyticsPageSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Website Analytics</h1>
        </div>
        <p style={styles.errorText}>{error}</p>
      </div>
    );
  }

  const visits = summary?.visits || {};
  const visitsByPage = summary?.visitsByPage || [];
  const visitsByCategory = summary?.visitsByCategory || [];
  const cartByProduct = summary?.cartByProduct || [];
  const checkoutByProduct = summary?.checkoutByProduct || [];

  const statCards = [
    { label: 'Visits', value: visits.daily ?? 0, sub: 'in selected range' },
    { label: 'Add to cart', value: summary?.totalCart ?? 0, sub: 'in selected range' },
    { label: 'Checkouts', value: summary?.totalCheckout ?? 0, sub: 'in selected range' },
    { label: 'Orders', value: summary?.totalOrders ?? 0, sub: 'in selected range' },
  ];

  const maxLegendChars = 28;
  const pieData = visitsByPage.slice(0, 8).map((item) => {
    const path = item.path || '/';
    const name = pagePathToLabel(path, item.productName);
    const shortName = name.length > maxLegendChars ? name.slice(0, maxLegendChars - 1) + '…' : name;
    return { name, shortName, value: item.count, fullPath: path };
  });

  const chartData = visitsOverTime.length > 0 ? visitsOverTime : [];
  const categoryChartData = categoryOverTime.data || [];
  const categoryColors = ['#F9D978', '#3FD5D1', '#A78BFA', '#34D399', '#F472B6', '#F27069', '#60A5FA'];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Website Analytics</h1>
        <p style={styles.headerSubtitle}>Track visits, categories, cart and checkouts — filter by date range</p>
      </div>

      <div style={styles.dateRangeCard}>
        <h3 style={styles.dateRangeTitle}>📅 Date range</h3>
        <p style={styles.dateRangeDesc}>By default only today’s data is shown. Select a date range and click Apply to see data for that period across all sections.</p>
        <div style={styles.dateRangeRow}>
          <label style={styles.dateLabel}>
            <span style={styles.dateLabelText}>From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={styles.dateInput}
            />
          </label>
          <label style={styles.dateLabel}>
            <span style={styles.dateLabelText}>To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={styles.dateInput}
            />
          </label>
          <div style={styles.dateRangeActions}>
            <button type="button" onClick={applyDateRange} style={styles.applyBtn}>
              Apply
            </button>
            <button
              type="button"
              onClick={resetToToday}
              style={styles.clearBtn}
            >
              Reset to today
            </button>
          </div>
        </div>
        <p style={styles.rangeSummary}>Showing data from <strong>{appliedFrom}</strong> to <strong>{appliedTo}</strong></p>
      </div>

      <div style={styles.statRow}>
        {statCards.map((card, index) => (
          <div key={index} style={styles.statBox}>
            <p style={styles.statValue}>{Number(card.value).toLocaleString()}</p>
            <p style={styles.statLabel}>{card.label}</p>
            <p style={styles.statSub}>{card.sub}</p>
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionHeaderBlock}>
          <h2 style={styles.sectionTitle}>Visits over time</h2>
          <p style={styles.sectionDesc}>Page views per day for the selected date range above.</p>
        </div>
        <div style={styles.chartWrap}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={chartData} margin={{ top: 16, right: 24, left: 16, bottom: 24 }}>
                <defs>
                  <linearGradient id="analyticsVisitsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F9D978" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#F9D978" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis dataKey="label" stroke="#888" tickLine={false} axisLine={false} tick={{ fill: '#aaa', fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis stroke="#888" tickLine={false} axisLine={false} tick={{ fill: '#aaa', fontSize: 12 }} allowDecimals={false} width={32} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#F9D978', marginBottom: 6 }} formatter={(value) => [value, 'Page views']} labelFormatter={(label) => `Date: ${label}`} />
                <Area type="monotone" dataKey="visits" stroke="#F9D978" strokeWidth={2} fill="url(#analyticsVisitsGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.emptyChart}>
              <span style={styles.emptyChartIcon}>📈</span>
              <p style={styles.emptyChartText}>No visit data for this period.</p>
              <p style={styles.emptyChartSub}>Select a date range or clear filter to see recent data.</p>
            </div>
          )}
        </div>
      </div>

      {visitsByCategory.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeaderBlock}>
            <h2 style={styles.sectionTitle}>Visits by category</h2>
            <p style={styles.sectionDesc}>Traffic from shop pages like /shop?category=Women or Men. “Other” = Home, cart, product pages, etc.</p>
          </div>
          <div style={styles.twoCol}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Category</th>
                    <th style={styles.thRight}>Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {visitsByCategory.map((row, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.td}>{row.category === 'Other' ? 'Other (home, cart, etc.)' : row.category}</td>
                      <td style={styles.tdRight}>{row.count?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={styles.pieCard}>
              <p style={styles.pieTitle}>Share by category</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={visitsByCategory.slice(0, 6).map((c, i) => ({ name: c.category === 'Other' ? 'Other' : c.category, value: c.count }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                  >
                    {visitsByCategory.slice(0, 6).map((_, index) => (
                      <Cell key={index} fill={categoryColors[index % categoryColors.length]} stroke="#1a1a1a" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(value) => [value.toLocaleString(), 'Visits']} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} formatter={(value) => <span style={{ color: '#ccc' }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {(cartByProduct.length > 0 || checkoutByProduct.length > 0) && (
        <div style={styles.section}>
          <div style={styles.sectionHeaderBlock}>
            <h2 style={styles.sectionTitle}>Cart & checkout by product</h2>
            <p style={styles.sectionDesc}>Which products were added to cart and which went to checkout (with product names).</p>
          </div>
          <div style={styles.twoCol}>
            {cartByProduct.length > 0 && (
              <div style={styles.tableWrap}>
                <h3 style={styles.subSectionTitle}>Add to cart by product</h3>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Product name</th>
                      <th style={styles.thRight}>Times added</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartByProduct.slice(0, 15).map((row, i) => (
                      <tr key={i} style={styles.tr}>
                        <td style={styles.td}>{row.productName || '—'}</td>
                        <td style={styles.tdRight}>{row.count?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {checkoutByProduct.length > 0 && (
              <div style={styles.tableWrap}>
                <h3 style={styles.subSectionTitle}>Products at checkout</h3>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Product name</th>
                      <th style={styles.thRight}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkoutByProduct.slice(0, 15).map((row, i) => (
                      <tr key={i} style={styles.tr}>
                        <td style={styles.td}>{row.productName || '—'}</td>
                        <td style={styles.tdRight}>{row.count?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {categoryChartData.length > 0 && categoryOverTime.categories.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeaderBlock}>
            <h2 style={styles.sectionTitle}>Category visits by day</h2>
            <p style={styles.sectionDesc}>Kis category ko kis din kitni visits hui — breakdown per day for selected date range.</p>
          </div>
          <div style={styles.chartWrap}>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={categoryChartData} margin={{ top: 16, right: 24, left: 16, bottom: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis dataKey="label" stroke="#888" tickLine={false} axisLine={false} tick={{ fill: '#aaa', fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis stroke="#888" tickLine={false} axisLine={false} tick={{ fill: '#aaa', fontSize: 12 }} allowDecimals={false} width={32} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend formatter={(value) => <span style={{ color: '#ccc', fontSize: 12 }}>{value}</span>} />
                {categoryOverTime.categories.slice(0, 6).map((cat, i) => (
                  <Bar key={cat} dataKey={cat} stackId="a" fill={categoryColors[i % categoryColors.length]} radius={[0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionHeaderBlock}>
          <h2 style={styles.sectionTitle}>Visits by page</h2>
          <p style={styles.sectionDesc}>Clean page names (UTM and tracking params removed). Which pages get the most traffic.</p>
        </div>
        {visitsByPage.length > 0 ? (
          <div style={styles.twoCol}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Page</th>
                    <th style={styles.thRight}>Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {visitsByPage.slice(0, 15).map((row, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.tdPage} title={row.path || '/'}>
                        {pagePathToLabel(row.path || '/', row.productName)}
                      </td>
                      <td style={styles.tdRight}>{row.count?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pieData.length > 0 && (
              <div style={styles.pieCard}>
                <p style={styles.pieTitle}>Top pages (share)</p>
                <div style={styles.pieChartRow}>
                  <div style={styles.pieChartOnly}>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={52}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          isAnimationActive={true}
                          label={false}
                        >
                          {pieData.map((_, index) => (
                            <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="#1a1a1a" strokeWidth={1} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={tooltipStyle}
                          formatter={(value, name, props) => [value.toLocaleString() + ' visits', props?.payload?.fullPath ? `${name}\n${props.payload.fullPath}` : name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul style={styles.pieLegendList}>
                    {pieData.map((entry, index) => (
                      <li key={index} style={styles.pieLegendItem} title={entry.name}>
                        <span style={{ ...styles.pieLegendDot, backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                        <span style={styles.pieLegendText}>{entry.shortName}</span>
                        <span style={styles.pieLegendCount}>{entry.value.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={styles.emptyChart}>
            <span style={styles.emptyChartIcon}>📄</span>
            <p style={styles.emptyChartText}>No page data yet.</p>
            <p style={styles.emptyChartSub}>Page views (including /shop?category=...) will show once the website sends analytics.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const tooltipStyle = {
  backgroundColor: '#2a2a2a',
  border: '1px solid #444',
  color: '#eee',
  borderRadius: '10px',
  padding: '12px 16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const styles = {
  container: { fontFamily: "'Inter', -apple-system, sans-serif", backgroundColor: '#E5D0A5', color: '#000', padding: '24px', minHeight: '100vh', boxSizing: 'border-box' },
  header: { marginBottom: '20px' },
  headerTitle: { fontSize: '1.375em', margin: 0, fontWeight: '700', color: '#000', letterSpacing: '-0.02em' },
  headerSubtitle: { fontSize: '0.95em', color: '#333', margin: '6px 0 0 0' },
  dateRangeCard: { backgroundColor: '#000', padding: '20px 24px', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
  dateRangeTitle: { margin: 0, color: '#F9D978', fontSize: '1.1em', fontWeight: '600' },
  dateRangeDesc: { margin: '6px 0 16px 0', color: '#888', fontSize: '0.85em' },
  dateRangeRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px' },
  dateLabel: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' },
  dateLabelText: { color: '#ccc', fontSize: '0.85em', fontWeight: '500' },
  dateInput: { backgroundColor: '#111', border: '1px solid #444', color: '#F9D978', borderRadius: '8px', padding: '10px 12px', fontSize: '0.95em', outline: 'none', width: '100%', minWidth: 0, height: '42px', boxSizing: 'border-box' },
  dateRangeActions: { display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '8px' },
  applyBtn: { backgroundColor: '#F9D978', color: '#000', border: 'none', borderRadius: '8px', padding: '10px 24px', minWidth: '100px', height: '42px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9em', flexShrink: 0 },
  clearBtn: { backgroundColor: 'transparent', color: '#888', border: '1px solid #555', borderRadius: '8px', padding: '10px 24px', minWidth: '100px', height: '42px', cursor: 'pointer', fontSize: '0.9em', flexShrink: 0 },
  rangeSummary: { margin: '14px 0 0 0', color: '#888', fontSize: '0.85em' },
  statRow: { display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '28px' },
  statBox: { backgroundColor: '#000', padding: '18px 20px', borderRadius: '16px', minWidth: '120px', flex: '1 1 120px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
  statValue: { fontSize: '1.25em', fontWeight: '700', color: '#F9D978', margin: 0, letterSpacing: '-0.02em' },
  statLabel: { fontSize: '0.85em', color: 'rgba(249, 217, 120, 0.95)', margin: '6px 0 0 0', fontWeight: '500' },
  statSub: { fontSize: '0.75em', color: 'rgba(249, 217, 120, 0.6)', margin: '2px 0 0 0' },
  section: { backgroundColor: '#000', padding: '24px', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' },
  sectionHeaderBlock: { marginBottom: '20px' },
  sectionTitle: { fontSize: '1rem', margin: 0, color: '#F9D978', fontWeight: '600', letterSpacing: '-0.01em' },
  sectionDesc: { fontSize: '0.8em', color: '#888', margin: '6px 0 0 0', maxWidth: '560px' },
  subSectionTitle: { fontSize: '1em', margin: '0 0 12px 0', color: '#E5D0A5', fontWeight: '600' },
  select: { backgroundColor: '#111', color: '#F9D978', border: '1px solid rgba(249, 217, 120, 0.5)', borderRadius: '12px', padding: '10px 18px', cursor: 'pointer', outline: 'none', fontSize: '0.9em' },
  chartWrap: { height: '340px', minHeight: '340px' },
  emptyChart: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '320px', color: '#666' },
  emptyChartIcon: { fontSize: '32px', marginBottom: '12px', opacity: 0.7 },
  emptyChartText: { margin: 0, fontSize: '1em', color: '#888' },
  emptyChartSub: { margin: '6px 0 0 0', fontSize: '0.85em', color: '#555' },
  errorText: { color: '#c00', padding: '20px' },
  twoCol: { display: 'flex', flexWrap: 'wrap', gap: '28px', alignItems: 'flex-start' },
  tableWrap: { flex: '1 1 280px', overflowX: 'auto', borderRadius: '12px', overflow: 'hidden', border: '1px solid #222' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '14px 18px', textAlign: 'left', color: '#E5D0A5', fontWeight: '600', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#111', borderBottom: '1px solid #222' },
  thRight: { padding: '14px 18px', textAlign: 'right', color: '#E5D0A5', fontWeight: '600', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#111', borderBottom: '1px solid #222' },
  tr: { borderBottom: '1px solid #1a1a1a' },
  td: { padding: '14px 18px', color: '#e0e0e0', fontSize: '0.9em' },
  tdPage: { padding: '14px 18px', color: '#e0e0e0', fontSize: '0.9em', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tdRight: { padding: '14px 18px', color: '#F9D978', fontSize: '0.9em', textAlign: 'right', fontWeight: '500' },
  pieCard: { flex: '0 0 340px', padding: '16px', borderRadius: '12px', backgroundColor: '#0a0a0a', border: '1px solid #222' },
  pieTitle: { margin: '0 0 12px 0', fontSize: '0.85em', color: '#888', textAlign: 'center' },
  pieChartRow: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  pieChartOnly: { width: '100%', maxWidth: 200, height: 200 },
  pieLegendList: { listStyle: 'none', margin: 0, padding: 0, width: '100%', maxWidth: 300 },
  pieLegendItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #222', fontSize: '12px' },
  pieLegendDot: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
  pieLegendText: { color: '#ccc', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pieLegendCount: { color: '#F9D978', fontWeight: '600', flexShrink: 0 },
};

export default Analytics;
